import { Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { createListStore } from "../../common/listStore";
import { renderListStore } from "../../common/listStoreUi";
import { renderClose } from "../../common/dialog";
import { useState } from "react";
import { CustomerDto, newPayment, PaymentEditDto, PaymentInvoiceDto } from "./types";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { propDateField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { propCurrency, propEmptyOr, propMinValueExclusive, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { customerDialogTitle, useCustomerBasicInfo } from "./common";
import { useInvoiceStates } from "../../common/enum";
import { dayjs } from "../../common/config";

export const PaymentDialog = (props: {
    close: () => void;
    useCustomerStore: UseEntityStore<CustomerDto>;
    onPaymentCreated(): void;
}) => {
    const customer = useCustomerBasicInfo(props.useCustomerStore);
    const [{ usePaymentStore, useInvoiceListStore }] = useState(() => {
        const usePaymentStore = createEntityStore<PaymentEditDto>({
            data: newPayment(), path: "customers/payments", id: null, getNewId: p => p.id!, saveProps: ["invcId", "payAmt", "payDte", "orderShipmentId"],
            onCreated() {
                props.onPaymentCreated();
                props.close();
            },
            propValidators: [
                propRequired("invcId", "Select invoice on right side."),

                propValidValue("payAmt", "payAmtFormatted"),
                propRequired("payAmt"),
                propCurrency("payAmt"),
                propMinValueExclusive("payAmt", 0),

                propValidValue("payDte", "payDteFormatted"),
                propRequired("payDte"),
                propEmptyOr("payDte", propValueValidator("payDte", p => dayjs.tz(p).isAfter(dayjs.tz(undefined).startOf("day")) && "Date must not be in future.")),
            ]
        });
        const useInvoiceListStore = createListStore<PaymentInvoiceDto>({
            path: `customers/payments/getInvoices?customerId=${customer.id}`, getId: p => JSON.stringify(p), listProps: [
                { column: "invcDte", label: "Invoice date", width: 120, type: "date" },
                { column: "invcId", label: "Invoice number", width: 120 },
                { column: "invcAmt", label: "Payment amount", width: 120, type: "currency" },
                { column: "invoiceTypeCode", label: "Invoice type", width: 100 },
                { column: "invoiceStateId", label: "Invoice status", flex: 1, enumStore: useInvoiceStates },
            ]
        });
        useInvoiceListStore.getState().selectedItem$.subscribe(item => {
            usePaymentStore.getState().setClientDataProp("invcId", item ? item.invcId : "");
            usePaymentStore.getState().setClientDataProp("orderShipmentId", item ? item.orderShipmentId : null);
            if (item) {
                usePaymentStore.getState().setClientDataProp("payAmt", item.invcAmt);
                usePaymentStore.getState().setClientDataProp("payAmtFormatted", undefined);
            }
        });
        return { usePaymentStore, useInvoiceListStore };
    });
    const { save, isSaveDisabled } = usePaymentStore(p => ({ save: p.save, isSaveDisabled: p.isSaveDisabled }));
    return <>
        <DialogTitle>{customerDialogTitle("New payment", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={4} alignItems="stretch">
                <Grid container item xs={3} columnSpacing={2} alignSelf="flex-start">
                    <Grid item xs={12}>{propDateField(usePaymentStore)("payDte", "payDteFormatted", "Payment date")}</Grid>
                    <Grid item xs={12}>{propTextField(usePaymentStore)("invcId", "Invoice number", { disabled: true })}</Grid>
                    <Grid item xs={12}>{propNumberField(usePaymentStore)("payAmt", "payAmtFormatted", "Payment amount", { isCurrency: true })}</Grid>
                </Grid>
                <Grid container item xs={9}>
                    {renderListStore(useInvoiceListStore)}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button disabled={isSaveDisabled} onClick={save} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
};
